import { z } from 'zod';
import { CountryCodeSchema, CurrencySchema } from './common';

/**
 * What a country means for the interface.
 *
 * The core does not know what a CUIT is, what "GRAVADO" means, or which IVA
 * rates exist — and it must not, because none of that survives a border. Chile
 * identifies taxpayers with a RUT, calls a taxed operation `AFECTO`, has one
 * IVA rate instead of four, and has no invoice letters at all. The vocabulary
 * belongs to the country pack, and this is how the pack reaches a screen.
 *
 * A workspace fetches its own profile once and drives every tax-shaped form
 * from it: the rate picker on a product, the regime picker on a counterparty,
 * the identifier validation, the province list, the fiscal settings form.
 * Nothing in the interface hard-codes a list any more, which is what stops
 * "add a country" from meaning "edit the front end".
 *
 * Validation *functions* cannot cross the wire, so each identifier type
 * carries the `errorKey` to show when the server rejects a value; the server
 * validates against the pack it holds.
 */

/** A value the user picks, with the label to show for it. */
export const CountryOptionValueSchema = z.object({
  code: z.string(),
  label: z.string(),
  /** The authority's own code, when a filing needs one. */
  externalCode: z.string().nullable(),
});
export type CountryOptionValue = z.infer<typeof CountryOptionValueSchema>;

/** A taxpayer's standing: `RESPONSABLE_INSCRIPTO`, `AFECTO`, ... */
export const TaxRegimeOptionSchema = CountryOptionValueSchema.extend({
  /** Whether this regime can issue a fiscal document at all. */
  isIssuer: z.boolean(),
});
export type TaxRegimeOption = z.infer<typeof TaxRegimeOptionSchema>;

/** How a taxpayer is identified: `CUIT`, `RUT`, `CNPJ`, ... */
export const TaxIdentifierOptionSchema = CountryOptionValueSchema.extend({
  /** False for `NONE`, which stands alone. */
  requiresValue: z.boolean(),
  /** Whether a value of this type has to be unique per workspace. */
  unique: z.boolean(),
  /** i18n key shown when the value does not validate. */
  errorKey: z.string(),
});
export type TaxIdentifierOption = z.infer<typeof TaxIdentifierOptionSchema>;

/** A province, region, state or department. */
export const JurisdictionOptionSchema = z.object({
  code: z.string(),
  name: z.string(),
});
export type JurisdictionOption = z.infer<typeof JurisdictionOptionSchema>;

/**
 * One field of the country's fiscal profile.
 *
 * The settings screen renders these instead of hard-coding a form per country.
 * `kind` is a small closed set on purpose: anything richer would be a component
 * the pack cannot own without importing the interface framework.
 */
export const SettingsFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: z.enum(['text', 'decimal', 'integer', 'select', 'date']),
  required: z.boolean(),
  options: z.array(CountryOptionValueSchema).optional(),
  hint: z.string().optional(),
  /**
   * Render this field only while another field holds a given value.
   *
   * Argentina's Monotributo category means nothing to a responsable
   * inscripto, so the pack hides it rather than asking a question whose answer
   * it will discard. The interface has to know that, or it draws every field
   * the pack declares and the pack's own opinion is lost.
   */
  showWhen: z.object({ field: z.string(), equals: z.string() }).optional(),
});
export type SettingsField = z.infer<typeof SettingsFieldSchema>;

/**
 * The whole vocabulary of one country, ready to render.
 *
 * Sent with the session-relevant locale, so a Chilean workspace read in
 * English gets English labels over Chilean values.
 */
export const CountryProfileSchema = z.object({
  code: CountryCodeSchema,
  name: z.string(),
  /** Pack revision the workspace was seeded with. */
  packVersion: z.string(),
  /** ISO-4217 code prices are quoted in. */
  currency: CurrencySchema,
  timezone: z.string(),
  /** Interface language the pack prefers. */
  locale: z.string(),

  tax: z.object({
    /** What this country calls the tax: `IVA`, `IGV`, `VAT`. */
    name: z.string(),
    /** And in a code, as a document line's tax breakdown stores it. */
    code: z.string(),
    rates: z.array(CountryOptionValueSchema),
    defaultRate: z.string(),
    regimes: z.array(TaxRegimeOptionSchema),
    defaultRegime: z.string(),
    treatments: z.array(CountryOptionValueSchema),
    defaultTreatment: z.string(),
    /** Applied to a walk-in consumer; falls back to `defaultRegime`. */
    consumerRegime: z.string(),
  }),

  identity: z.object({
    types: z.array(TaxIdentifierOptionSchema),
    defaultType: z.string(),
    /** Applied to a walk-in consumer; falls back to `defaultType`. */
    consumerType: z.string(),
  }),

  jurisdictions: z.array(JurisdictionOptionSchema),

  documents: z.object({
    types: z.array(CountryOptionValueSchema),
    defaultType: z.string(),
  }),

  /** The fiscal profile form, as the pack defines it. */
  settings: z.array(SettingsFieldSchema),
});
export type CountryProfile = z.infer<typeof CountryProfileSchema>;

/* -------------------------------------------------------------------- *
 * Taxpayer lookup
 * -------------------------------------------------------------------- */

/**
 * A taxpayer as a national registry publishes them.
 *
 * Mirrors `TaxpayerRecord` in `@arriba.digital/countries`, which is where the
 * lookup itself is declared. Only the identity is required: registries differ
 * in what they publish, and a lookup that returns a name but no address is
 * still worth the round trip.
 */
export const TaxpayerRecordSchema = z.object({
  taxId: z.string(),
  taxIdType: z.string(),
  legalName: z.string(),
  /** The country's own regime code, resolved against `profile.tax.regimes`. */
  regime: z.string().optional(),
  address: z.string().optional(),
  jurisdiction: z.string().optional(),
});
export type TaxpayerRecord = z.infer<typeof TaxpayerRecordSchema>;

/**
 * The answer to "do you know this taxpayer?".
 *
 * `available` is separate from `record` on purpose, and it is the more
 * important of the two. A country whose registry this deployment has not been
 * wired to, or one that is simply down, both answer `null` for the record —
 * but only the first means the interface should not offer a lookup button at
 * all. Collapsing them would either hide a working button or show a dead one,
 * and the operator would have no way to tell which.
 */
export const TaxpayerLookupResultSchema = z.object({
  available: z.boolean(),
  record: TaxpayerRecordSchema.nullable(),
});
export type TaxpayerLookupResult = z.infer<typeof TaxpayerLookupResultSchema>;
